import { useState, type FormEvent, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HashtagIcon,
  SparklesIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import { createChannel } from '@/services/channelApi';
import type { CreateChannelRequest } from '@/types/channel';
import Input from '@/components/ui/Input';
import { getErrorMessage } from '@/utils/errors';

/** Convert a display name to a URL-safe slug */
function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 48);
}

export default function CreateChannelPage() {
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [sportFocus, setSportFocus] = useState('');
  const [listed, setListed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** D11: true when the backend returns 402 (subscription required) */
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);

  const handleDisplayNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setDisplayName(name);
    if (!slugManuallyEdited) {
      setSlug(toSlug(name));
    }
  };

  const handleSlugChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSlugManuallyEdited(true);
    setSlug(toSlug(e.target.value));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !slug.trim()) return;

    setError(null);
    setIsSubmitting(true);

    const payload: CreateChannelRequest = {
      slug: slug.trim(),
      display_name: displayName.trim(),
      listed,
    };
    if (description.trim()) payload.description = description.trim();
    if (sportFocus.trim()) payload.sport_focus = sportFocus.trim();

    try {
      const channel = await createChannel(payload);
      navigate(`/channels/${channel.slug}`);
    } catch (err) {
      // D11: 402 = subscription required — show subscribe CTA instead of generic error
      if (isAxiosError(err) && err.response?.status === 402) {
        setSubscriptionRequired(true);
        setIsSubmitting(false);
        return;
      }
      setError(getErrorMessage(err));
      setIsSubmitting(false);
    }
  };

  const isValid = displayName.trim().length > 0 && slug.trim().length > 0;

  // D11: Subscription required — show a dedicated CTA card
  if (subscriptionRequired) {
    return (
      <div
        style={{ maxWidth: '480px', margin: '0 auto', paddingTop: '32px' }}
        data-testid="subscribe-cta"
      >
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid rgba(244,201,93,0.25)',
            borderRadius: '20px',
            padding: '36px 32px',
            boxShadow: 'var(--shadow-card)',
            textAlign: 'center',
          }}
        >
          {/* Lock icon */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              backgroundColor: 'rgba(244,201,93,0.10)',
              border: '1px solid rgba(244,201,93,0.22)',
              marginBottom: '20px',
            }}
          >
            <LockClosedIcon
              style={{ width: '24px', height: '24px', color: 'var(--color-accent-gold)' }}
            />
          </div>

          <h1
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '20px',
              color: 'var(--color-text-primary)',
              margin: '0 0 10px',
              letterSpacing: '-0.2px',
            }}
          >
            Subscription required
          </h1>

          <p
            style={{
              fontSize: '14px',
              color: 'var(--color-text-muted)',
              margin: '0 0 28px',
              lineHeight: 1.6,
            }}
          >
            Creating a channel requires an active subscription.
            Subscribe to unlock channels, seat management, and athlete progress tracking.
          </p>

          <Link
            to="/subscribe"
            data-testid="subscribe-link"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 28px',
              borderRadius: '12px',
              backgroundColor: 'var(--color-accent-gold)',
              color: '#0B0F14',
              fontSize: '15px',
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              textDecoration: 'none',
              transition: 'opacity 150ms ease',
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.88')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
          >
            View subscription plans
          </Link>

          <div style={{ marginTop: '16px' }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                color: 'var(--color-text-muted)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                padding: 0,
                transition: 'color 150ms ease',
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-secondary)')
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)')
              }
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes cc-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cc-page { animation: cc-fade-up 0.42s cubic-bezier(0.22, 1, 0.36, 1) both; }

        .cc-create-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .cc-create-btn:not(:disabled):hover {
          background-color: var(--color-accent-hover) !important;
          box-shadow: 0 0 20px rgba(30, 215, 96, 0.32), 0 4px 12px rgba(0,0,0,0.22);
          transform: translateY(-1px);
        }
        .cc-create-btn:not(:disabled):active { transform: scale(0.97); }

        .cc-input-wrap input,
        .cc-input-wrap textarea {
          transition: border-color 180ms ease, box-shadow 180ms ease;
        }
        .cc-input-wrap input:focus,
        .cc-input-wrap textarea:focus {
          box-shadow: 0 0 0 3px rgba(30, 215, 96, 0.10);
        }

        .cc-listed-row {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px;
          border-radius: 12px;
          background-color: var(--color-dark-elevated-2);
          border: 1px solid var(--color-border-subtle);
          transition: border-color 150ms ease;
        }
        .cc-listed-row:hover {
          border-color: var(--color-border-strong);
        }
        .cc-toggle-switch {
          position: relative;
          flex-shrink: 0;
          width: 40px;
          height: 22px;
          margin-top: 2px;
        }
        .cc-toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
          position: absolute;
        }
        .cc-toggle-track {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          cursor: var(--cursor-hand);
          transition: background-color 180ms ease;
        }
        .cc-toggle-track::after {
          content: '';
          position: absolute;
          top: 3px;
          left: 3px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          transition: transform 180ms ease;
        }
        .cc-slug-prefix {
          display: flex;
          align-items: center;
          padding: 0 10px 0 12px;
          height: 40px;
          border-radius: 10px 0 0 10px;
          border: 1px solid var(--color-border-subtle);
          border-right: none;
          background-color: var(--color-dark-elevated-1);
          color: var(--color-text-muted);
          font-size: 13px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .cc-slug-input {
          flex: 1;
          height: 40px;
          border-radius: 0 10px 10px 0 !important;
        }
      `}</style>

      <div
        className="cc-page"
        style={{ maxWidth: '540px', margin: '0 auto', paddingTop: '16px' }}
      >
        {/* Page header */}
        <div style={{ marginBottom: '28px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              backgroundColor: 'rgba(30,215,96,0.12)',
              border: '1px solid rgba(30,215,96,0.22)',
              marginBottom: '14px',
            }}
          >
            <SparklesIcon
              style={{ width: '22px', height: '22px', color: 'var(--color-accent)' }}
            />
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '22px',
              color: 'var(--color-text-primary)',
              margin: '0 0 6px',
              letterSpacing: '-0.3px',
            }}
          >
            Create a Channel
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0 }}>
            Channels let you share templates with your athletes.
          </p>
        </div>

        {/* Card */}
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <form onSubmit={handleSubmit} noValidate>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Channel name */}
              <div className="cc-input-wrap">
                <Input
                  label="Channel Name"
                  id="cc-display-name"
                  value={displayName}
                  onChange={handleDisplayNameChange}
                  placeholder="e.g. Elite Fencing Academy"
                  required
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>

              {/* Slug */}
              <div>
                <label
                  htmlFor="cc-slug"
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: '4px',
                  }}
                >
                  Channel URL
                </label>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <span className="cc-slug-prefix">
                    <HashtagIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
                    channels/
                  </span>
                  <div style={{ flex: 1 }} className="cc-input-wrap">
                    <input
                      id="cc-slug"
                      value={slug}
                      onChange={handleSlugChange}
                      placeholder="my-channel"
                      required
                      disabled={isSubmitting}
                      className="cc-slug-input"
                      style={{
                        width: '100%',
                        height: '40px',
                        borderRadius: '0 10px 10px 0',
                        border: '1px solid var(--color-border-subtle)',
                        backgroundColor: 'var(--color-dark-elevated-2)',
                        color: 'var(--color-text-primary)',
                        padding: '0 12px',
                        fontSize: '14px',
                        outline: 'none',
                        fontFamily: 'var(--font-body)',
                        transition: 'border-color 180ms ease',
                        boxSizing: 'border-box',
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = 'var(--color-accent)')
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor = 'var(--color-border-subtle)')
                      }
                    />
                  </div>
                </div>
                <p
                  style={{
                    marginTop: '5px',
                    fontSize: '12px',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Used in the channel URL. Can only contain letters, numbers, and hyphens.
                </p>
              </div>

              {/* Description */}
              <div className="cc-input-wrap">
                <label
                  htmlFor="cc-description"
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: '4px',
                  }}
                >
                  Description{' '}
                  <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>
                    (optional)
                  </span>
                </label>
                <textarea
                  id="cc-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell athletes what this channel is about..."
                  rows={3}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    borderRadius: '10px',
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-dark-elevated-2)',
                    color: 'var(--color-text-primary)',
                    padding: '10px 12px',
                    fontSize: '14px',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'var(--font-body)',
                    transition: 'border-color 180ms ease',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = 'var(--color-accent)')
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = 'var(--color-border-subtle)')
                  }
                />
              </div>

              {/* Sport focus */}
              <div className="cc-input-wrap">
                <Input
                  label="Sport Focus (optional)"
                  id="cc-sport-focus"
                  value={sportFocus}
                  onChange={(e) => setSportFocus(e.target.value)}
                  placeholder="e.g. Fencing, Swimming, Tennis"
                  disabled={isSubmitting}
                />
              </div>

              {/* Listed toggle */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: '8px',
                  }}
                >
                  Discovery
                </label>

                <label className="cc-listed-row" htmlFor="cc-listed-toggle">
                  {/* Toggle switch */}
                  <span className="cc-toggle-switch">
                    <input
                      id="cc-listed-toggle"
                      data-testid="cc-listed-toggle"
                      type="checkbox"
                      checked={listed}
                      onChange={(e) => setListed(e.target.checked)}
                      disabled={isSubmitting}
                    />
                    <span
                      className="cc-toggle-track"
                      style={{
                        backgroundColor: listed ? 'var(--color-accent)' : 'var(--color-border-strong)',
                      }}
                    >
                      <span
                        style={{
                          content: "''",
                          position: 'absolute',
                          top: '3px',
                          left: '3px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: 'white',
                          transition: 'transform 180ms ease',
                          transform: listed ? 'translateX(18px)' : 'translateX(0)',
                        }}
                      />
                    </span>
                  </span>

                  {/* Label + description */}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                        marginBottom: '3px',
                      }}
                    >
                      List this school publicly
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
                      {listed
                        ? 'Your school will appear in discovery and have a public landing page with a "Request to join" button. No content (templates or evaluations) is ever visible to non-members.'
                        : 'Your school is completely private — reachable only by invite link. It will not appear in discovery or have a public landing page.'}
                    </span>
                  </span>
                </label>
              </div>

              {/* Error */}
              {error && (
                <div
                  role="alert"
                  style={{
                    borderRadius: '10px',
                    border: '1px solid rgba(240, 93, 94, 0.3)',
                    backgroundColor: 'rgba(240, 93, 94, 0.08)',
                    padding: '12px 14px',
                    fontSize: '13px',
                    color: 'var(--color-danger)',
                    lineHeight: 1.5,
                  }}
                >
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !isValid}
                className="cc-create-btn"
                style={{
                  width: '100%',
                  height: '46px',
                  borderRadius: '12px',
                  backgroundColor: 'var(--color-accent)',
                  border: 'none',
                  color: 'var(--color-text-inverse)',
                  fontSize: '15px',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  cursor: isSubmitting || !isValid ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting || !isValid ? 0.55 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '4px',
                }}
              >
                {isSubmitting ? (
                  <>
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(0,0,0,0.2)',
                        borderTopColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'spin 0.7s linear infinite',
                      }}
                    />
                    Creating...
                  </>
                ) : (
                  'Create Channel'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Cancel link */}
        <p
          style={{
            textAlign: 'center',
            marginTop: '20px',
            fontSize: '13px',
            color: 'var(--color-text-muted)',
          }}
        >
          Changed your mind?{' '}
          <button
            onClick={() => navigate(-1)}
            style={{
              color: 'var(--color-accent)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              padding: 0,
              transition: 'color 150ms ease',
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color =
                'var(--color-accent-hover)')
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent)')
            }
          >
            Go back
          </button>
        </p>
      </div>
    </>
  );
}
